import React, { useState } from "react";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { Check, ArrowRight } from "lucide-react";

const QUESTIONS = ["q1", "q2", "q3", "q4", "q5"];
const KEYS = ["volatility", "horizon", "experience", "drawdown", "goal"];

export default function Profile() {
  const { t, lang } = useI18n();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const pick = async (value) => {
    const next = { ...answers, [KEYS[step]]: value };
    setAnswers(next);
    if (step < 4) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const res = await api.post("/risk-profile", { ...next, lang });
        setResult(res.data);
      } finally {
        setLoading(false);
      }
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
  };

  const progress = result ? 100 : ((step / 5) * 100).toFixed(0);
  const qKey = QUESTIONS[step];
  const question = t.risk[qKey];
  const opts = t.risk[`${qKey}_opts`] || [];

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-profile">
      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
          GOLDORA · Privacy-first
        </span>
        <h1 className="mt-2 font-display text-4xl lg:text-5xl tracking-tight">{t.risk.title}</h1>
        <p className="mt-3 text-neutral-400">{t.risk.sub}</p>
      </div>

      {/* Progress */}
      <div className="mt-8 h-1 rounded-full bg-white/5 overflow-hidden" data-testid="risk-progress">
        <div
          className="h-full bg-gold-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {!result && (
        <div className="mt-8 surface p-6 md:p-8" data-testid={`risk-step-${step}`}>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
            {step + 1} / 5
          </div>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl tracking-tight">{question}</h2>
          <div className="mt-6 space-y-2">
            {opts.map((opt, i) => {
              const value = i + 1;
              const selected = answers[KEYS[step]] === value;
              return (
                <button
                  key={i}
                  onClick={() => pick(value)}
                  data-testid={`risk-opt-${step}-${value}`}
                  disabled={loading}
                  className={`w-full text-left px-4 py-3 rounded-md border transition-all flex items-center justify-between group ${
                    selected
                      ? "bg-gold-500/15 border-gold-500/60 text-white"
                      : "bg-white/[0.03] border-white/10 text-neutral-300 hover:border-gold-500/40 hover:bg-white/5"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full border border-white/20 grid place-items-center text-xs font-mono">
                      {value}
                    </span>
                    {opt}
                  </span>
                  <ArrowRight
                    size={16}
                    className="opacity-0 group-hover:opacity-60 -translate-x-2 group-hover:translate-x-0 transition-all"
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-8 space-y-6" data-testid="risk-result">
          <div className="surface p-6 md:p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
              {t.risk.result}
            </div>
            <h2 className="mt-2 font-display text-5xl sm:text-6xl tracking-tight">{result.label}</h2>
            <div className="mt-3 text-sm text-neutral-400 max-w-xl">
              {t.risk[`profile_${result.profile}_desc`]}
            </div>
            <div className="mt-6 flex items-center gap-4">
              <div className="font-mono text-xs text-neutral-500 uppercase tracking-widest">
                Score: <span className="text-white">{result.score}/{result.max_score}</span>
              </div>
              <button
                onClick={reset}
                className="text-xs font-mono uppercase tracking-widest border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-md"
                data-testid="risk-reset"
              >
                {t.common.reset}
              </button>
            </div>
          </div>

          <div className="surface p-6 md:p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">
              {t.risk.allocation}
            </div>
            <div className="mt-4 space-y-3">
              {Object.entries(result.allocation).map(([k, v]) => {
                const label = {
                  bonds: lang === "pt" ? "Renda fixa" : "Fixed income",
                  stocks: lang === "pt" ? "Ações" : "Stocks",
                  real_estate: lang === "pt" ? "Imóveis" : "Real estate",
                  alternatives: lang === "pt" ? "Alternativos" : "Alternatives",
                }[k] || k;
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-white">{label}</span>
                      <span className="font-mono text-gold-400">{v}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gold-500" style={{ width: `${v}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
